"use client"

import { wrap } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useEffect, useRef, useState } from "react"
import {
  pauseGachaRevealWhoosh,
  playGachaRevealWhooshAt,
  syncGachaRevealWhooshToPlayback,
} from "./gacha-reveal-audio"
import { GACHA_REVEAL_FADE_MS } from "./gacha-reveal-handoff"
import type { WatchRevealPlan } from "./gacha-watch-reveal"

type GachaWatchRevealProps = {
  plan: Extract<WatchRevealPlan, { kind: "watch-single" }>
  cardImage: string
  cardName: string
  muted: boolean
  onFade: () => void
  onComplete: () => void
}

export const GachaWatchReveal = reatomComponent<GachaWatchRevealProps>(
  ({ cardImage, cardName, muted, onComplete, onFade, plan }) => {
    const [showGlow, setShowGlow] = useState(false)
    const [mountCard, setMountCard] = useState(false)
    const [showCard, setShowCard] = useState(false)
    const boxVideoRef = useRef<HTMLVideoElement>(null)
    const glowVideoRef = useRef<HTMLVideoElement>(null)
    const playbackSyncRef = useRef<() => void>(() => undefined)
    const boxPlayingRef = useRef(false)
    const mutedRef = useRef(muted)
    const onFadeRef = useRef(onFade)
    const onCompleteRef = useRef(onComplete)
    const glowVisibleRef = useRef(false)
    const cardMountedRef = useRef(false)
    const handoffStartedRef = useRef(false)
    const completeRef = useRef(false)

    useEffect(() => {
      onFadeRef.current = onFade
      onCompleteRef.current = onComplete
    }, [onComplete, onFade])

    useEffect(() => {
      mutedRef.current = muted
    }, [muted])

    useEffect(() => {
      let animationFrameId = 0
      let cardFrameId = 0
      let completeId = 0

      glowVisibleRef.current = false
      cardMountedRef.current = false
      handoffStartedRef.current = false
      completeRef.current = false
      boxPlayingRef.current = false
      setShowGlow(false)
      setMountCard(false)
      setShowCard(false)
      glowVideoRef.current?.pause()
      pauseGachaRevealWhoosh()

      const setGlowVisible = (visible: boolean) => {
        if (glowVisibleRef.current === visible) return

        glowVisibleRef.current = visible
        setShowGlow(visible)

        const glowVideo = glowVideoRef.current
        if (!glowVideo) return

        if (!visible) {
          glowVideo.pause()
          return
        }

        glowVideo.currentTime = 0
        void wrap(glowVideo.play()).catch(wrap(() => undefined))
      }

      const startHandoff = () => {
        if (handoffStartedRef.current) return

        handoffStartedRef.current = true
        onFadeRef.current()
        completeId = window.setTimeout(() => {
          if (completeRef.current) return

          completeRef.current = true
          onCompleteRef.current()
        }, GACHA_REVEAL_FADE_MS)
      }

      const syncToBoxPlayback = () => {
        const boxVideo = boxVideoRef.current
        if (!boxVideo) return

        const playbackMs = boxVideo.currentTime * 1000
        if (boxPlayingRef.current) {
          syncGachaRevealWhooshToPlayback(
            boxVideo.currentTime,
            mutedRef.current,
          )
        }
        setGlowVisible(
          playbackMs >= plan.glowStartsAtMs && playbackMs < plan.glowEndsAtMs,
        )

        if (playbackMs >= plan.cardStartsAtMs && !cardMountedRef.current) {
          cardMountedRef.current = true
          setMountCard(true)
          cardFrameId = window.requestAnimationFrame(() => setShowCard(true))
        }

        if (playbackMs >= plan.handoffAtMs) {
          startHandoff()
        }
      }

      const syncAnimationFrame = () => {
        syncToBoxPlayback()
        animationFrameId = window.requestAnimationFrame(syncAnimationFrame)
      }

      playbackSyncRef.current = syncToBoxPlayback
      animationFrameId = window.requestAnimationFrame(syncAnimationFrame)

      return () => {
        playbackSyncRef.current = () => undefined
        window.cancelAnimationFrame(animationFrameId)
        window.cancelAnimationFrame(cardFrameId)
        window.clearTimeout(completeId)
        glowVideoRef.current?.pause()
        pauseGachaRevealWhoosh()
      }
    }, [
      plan.cardStartsAtMs,
      plan.glowEndsAtMs,
      plan.glowStartsAtMs,
      plan.handoffAtMs,
    ])

    return (
      <div
        className="relative h-full w-full overflow-hidden"
        data-testid="watch-reveal"
      >
        <video
          ref={boxVideoRef}
          aria-label="Opened Watch box"
          autoPlay
          muted
          className="absolute inset-0 h-full w-full object-contain"
          playsInline
          preload="auto"
          src={plan.boxSrc}
          onEnded={() => {
            boxPlayingRef.current = false
            pauseGachaRevealWhoosh()
            playbackSyncRef.current()
          }}
          onPause={() => {
            boxPlayingRef.current = false
            pauseGachaRevealWhoosh()
          }}
          onPlaying={(event) => {
            boxPlayingRef.current = true
            playGachaRevealWhooshAt(
              event.currentTarget.currentTime,
              mutedRef.current,
            )
            playbackSyncRef.current()
          }}
          onTimeUpdate={() => playbackSyncRef.current()}
          onWaiting={() => {
            boxPlayingRef.current = false
            pauseGachaRevealWhoosh()
          }}
        />
        <div
          aria-hidden={!mountCard}
          aria-label={mountCard ? "NFT reveal frame" : undefined}
          className="absolute top-1/2 left-1/2 h-[min(72vh,62.5vmin,480px)] w-[min(82vw,47vmin,360px)] -translate-x-1/2 -translate-y-1/2 overflow-hidden p-3 transition-opacity ease-out"
          style={{
            opacity: showCard ? 1 : 0,
            transitionDuration: `${plan.cardFadeDurationMs}ms`,
          }}
        >
          <img
            alt={cardName}
            aria-label={mountCard ? "Revealed NFT" : undefined}
            className="h-full w-full object-contain"
            src={cardImage}
          />
        </div>
        <video
          ref={glowVideoRef}
          aria-hidden={!showGlow}
          aria-label={showGlow ? "Rarity glow" : undefined}
          muted
          className={`pointer-events-none absolute inset-0 h-full w-full object-contain ${showGlow ? "opacity-100" : "opacity-0"}`}
          playsInline
          preload="auto"
          src={plan.glowSrc}
        />
      </div>
    )
  },
  "GachaWatchReveal",
)
